import React, { createContext, useState, useEffect, useContext, useMemo } from 'react'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import algoliasearch from 'algoliasearch/lite'

import useMounted from '@/components/hooks/use-mounted'
import { Context } from '@/shared/store/reducers/global-reducer'
import { getAlgoliaApplicationId } from '@/config/algolia.config'
import { ProjectsFacetLabelLookupProvider } from '@/routes/projects/components/projects/projects-facet-label-lookup'

/** Same client as InstantSearch — used for filter-drawer hit-count preview queries. */
export const ProjectsAlgoliaSearchClientContext = createContext<ReturnType<typeof algoliasearch> | null>(null)

/** Controlled InstantSearch state setter — use functional updates so commits merge the latest `configure` / refinements. */
export const ProjectsSearchStateContext = createContext<React.Dispatch<React.SetStateAction<any>> | null>(null)

const ProjectsSearchContainer = (props: any) => {
  const {
    clientId,
    brandId,

    filterable,
    filters,
    searchPhrase,
    children,
  } = props

  const { store } = useContext(Context)
  const [refresh, setRefresh] = useState(false)
  const [searchState, setSearchState] = useState({ page: 1 } as any)

  const { algoliaSearchSecuredApiKey } = store

  const searchClient = useMemo(() => {
    return algoliaSearchSecuredApiKey
      ? algoliasearch(getAlgoliaApplicationId(), algoliaSearchSecuredApiKey)
      : null
  }, [algoliaSearchSecuredApiKey])

  useEffect(() => {
    if (!searchClient) {
      return
    }

    handleForceRefresh()

    return () => {}
  }, [searchClient, clientId, brandId])

  useEffect(() => {
    if (refresh) {
      setRefresh(false)
    }
    return () => {}
  }, [refresh])

  const onSearchStateChange = (nextState: any) => {
    setSearchState(nextState)
  }

  const handleForceRefresh = () => {
    setSearchState({
      page: 1,
    })
    setRefresh(true)
  }

  if (!searchClient) return null

  return (
    <ProjectsFacetLabelLookupProvider clientId={clientId}>
      <ProjectsAlgoliaSearchClientContext.Provider value={searchClient}>
        <ProjectsSearchStateContext.Provider value={setSearchState}>
          <InstantSearch
            searchClient={searchClient}
            indexName="Projects"
            refresh={refresh}
            stalledSearchDelay={500}
            searchState={searchState}
            onSearchStateChange={onSearchStateChange}
          >
            {filterable ? <Configure filters={filters} query={''} /> : <Configure query={searchPhrase} />}
            {children}
          </InstantSearch>
        </ProjectsSearchStateContext.Provider>
      </ProjectsAlgoliaSearchClientContext.Provider>
    </ProjectsFacetLabelLookupProvider>
  )
}

export default ProjectsSearchContainer
